/* Publieke inschrijfpagina — te embedden op de Webster marketing-website.
   Vult ouder + kind + locatie + dagen in, toont concept-contract, ouder tekent
   digitaal (akkoord-vinkje + naam + auto-zegel), en systeem maakt alle records aan. */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Baby, ArrowLeft, ArrowRight, Check, ShieldCheck, FileSignature, Sparkles } from "lucide-react";
import { toast, Toaster } from "sonner";

// Aparte axios-instance — publieke endpoints, GEEN credentials meesturen.
const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: false,
});

const DAGDELEN = [
  { value: "ochtend", label: "Ochtend" },
  { value: "middag", label: "Middag" },
  { value: "hele_dag", label: "Hele dag" },
  { value: "naschools", label: "Naschools (BSO)" },
];
const WEEKDAYS = [
  { i: 0, label: "Maandag" }, { i: 1, label: "Dinsdag" }, { i: 2, label: "Woensdag" },
  { i: 3, label: "Donderdag" }, { i: 4, label: "Vrijdag" },
];

export default function InschrijfPage() {
  const [step, setStep] = useState(1);
  const [locations, setLocations] = useState([]);
  const [signed, setSigned] = useState(null); // seal na tekenen
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    ouder: { voornaam: "", achternaam: "", email: "", telefoon: "", adres: "", postcode: "", stad: "", iban: "" },
    partner: null,
    kind: { voornaam: "", achternaam: "", geboortedatum: "", allergies: "", huisarts: "" },
    location_id: "",
    start_date: "",
    dagen: [], // [{weekday, dagdeel}]
    opmerkingen: "",
  });

  const [sign, setSign] = useState({
    akkoord: false,
    ondertekenaar_naam: "",
    ondertekenaar_2_naam: "",
  });

  useEffect(() => {
    api.get("/public/locations").then(r => setLocations(r.data)).catch(() => {});
  }, []);

  const locName = useMemo(() => locations.find(l => l.id === form.location_id)?.name || "—", [locations, form.location_id]);
  const urenPerWeek = useMemo(() => {
    const per = { ochtend: 5, middag: 5, hele_dag: 10, naschools: 4 };
    return form.dagen.reduce((s, d) => s + (per[d.dagdeel] || 5), 0);
  }, [form.dagen]);

  const toggleDag = (weekday, dagdeel) => {
    const key = `${weekday}-${dagdeel}`;
    const has = form.dagen.some(d => d.weekday === weekday && d.dagdeel === dagdeel);
    setForm(f => ({
      ...f,
      dagen: has
        ? f.dagen.filter(d => !(d.weekday === weekday && d.dagdeel === dagdeel))
        : [...f.dagen, { weekday, dagdeel }],
    }));
  };

  const nextStep = () => {
    if (step === 1) {
      if (!form.ouder.voornaam || !form.ouder.achternaam || !form.ouder.email || !form.ouder.telefoon) {
        toast.error("Vul alle verplichte oudergegevens in"); return;
      }
    }
    if (step === 2) {
      if (!form.kind.voornaam || !form.kind.achternaam || !form.kind.geboortedatum) {
        toast.error("Vul de kindgegevens in"); return;
      }
    }
    if (step === 3) {
      if (!form.location_id || !form.start_date || form.dagen.length === 0) {
        toast.error("Kies locatie, startdatum en minstens één dag"); return;
      }
    }
    setStep(s => s + 1);
  };

  const submit = async () => {
    if (!sign.akkoord || !sign.ondertekenaar_naam.trim()) {
      toast.error("Vink akkoord aan en vul uw volledige naam in"); return;
    }
    setBusy(true);
    try {
      const create = await api.post("/public/inschrijving", form);
      const iid = create.data.id;
      const signResp = await api.post(`/public/inschrijving/${iid}/sign`, sign);
      setSigned(signResp.data.seal);
      setStep(5);
      toast.success("Inschrijving ondertekend en verwerkt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Inschrijving mislukt");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[color:var(--bone)] subtle-grain">
      <Toaster richColors position="top-right" />
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "var(--sage)" }}>
            <Baby className="h-5 w-5 text-white" strokeWidth={2.4} />
          </div>
          <div>
            <div className="font-display text-lg leading-tight">Webster<span className="text-[color:var(--terracotta)]">.</span></div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Digitaal inschrijven</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {step < 5 && (
          <div className="mb-6 flex items-center gap-2" data-testid="step-indicator">
            {[1, 2, 3, 4].map(n => (
              <div key={n} className={`h-2 flex-1 rounded-full ${n <= step ? "bg-[color:var(--sage)]" : "bg-neutral-200"}`} />
            ))}
          </div>
        )}

        {step === 1 && (
          <Card className="p-5 sm:p-8 bg-white shadow-none">
            <h1 className="font-display text-2xl sm:text-3xl mb-1">Uw gegevens</h1>
            <p className="text-sm text-neutral-600 mb-6">Wij hebben uw contactgegevens nodig om het contract op te maken.</p>
            <OuderVelden value={form.ouder} onChange={v => setForm(f => ({ ...f, ouder: v }))} idPrefix="ouder" />
            <div className="mt-4">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={!!form.partner}
                  onCheckedChange={v => setForm(f => ({ ...f, partner: v ? { voornaam: "", achternaam: "", email: "", telefoon: "" } : null }))}
                  data-testid="add-partner"
                /> Tweede ouder / verzorger toevoegen
              </label>
              {form.partner && (
                <div className="mt-4">
                  <OuderVelden value={form.partner} onChange={v => setForm(f => ({ ...f, partner: v }))} idPrefix="partner" />
                </div>
              )}
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card className="p-5 sm:p-8 bg-white shadow-none">
            <h1 className="font-display text-2xl sm:text-3xl mb-1">Gegevens van uw kind</h1>
            <p className="text-sm text-neutral-600 mb-6">Zorg dat allergieën en bijzonderheden vermeld zijn.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Voornaam *" testid="kind-voornaam" value={form.kind.voornaam} onChange={v => setForm(f => ({ ...f, kind: { ...f.kind, voornaam: v } }))} />
              <Field label="Achternaam *" testid="kind-achternaam" value={form.kind.achternaam} onChange={v => setForm(f => ({ ...f, kind: { ...f.kind, achternaam: v } }))} />
              <Field label="Geboortedatum *" type="date" testid="kind-geboortedatum" value={form.kind.geboortedatum} onChange={v => setForm(f => ({ ...f, kind: { ...f.kind, geboortedatum: v } }))} />
              <Field label="Huisarts" testid="kind-huisarts" value={form.kind.huisarts} onChange={v => setForm(f => ({ ...f, kind: { ...f.kind, huisarts: v } }))} />
              <div className="col-span-1 sm:col-span-2">
                <Label>Allergieën / medische bijzonderheden</Label>
                <Textarea rows={2} data-testid="kind-allergies" value={form.kind.allergies} onChange={e => setForm(f => ({ ...f, kind: { ...f.kind, allergies: e.target.value } }))} />
              </div>
            </div>
          </Card>
        )}

        {step === 3 && (
          <Card className="p-5 sm:p-8 bg-white shadow-none">
            <h1 className="font-display text-2xl sm:text-3xl mb-1">Opvang gewenst</h1>
            <p className="text-sm text-neutral-600 mb-6">Kies locatie, startdatum en gewenste dagen/dagdelen. Groep wijzen wij zelf toe op basis van beschikbaarheid.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <div>
                <Label>Locatie *</Label>
                <Select value={form.location_id} onValueChange={v => setForm(f => ({ ...f, location_id: v }))}>
                  <SelectTrigger data-testid="location-select"><SelectValue placeholder="Kies locatie" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Gewenste startdatum *" type="date" testid="start-date" value={form.start_date} onChange={v => setForm(f => ({ ...f, start_date: v }))} />
            </div>
            <Label className="mb-2 block">Dagen & dagdelen *</Label>
            <div className="overflow-x-auto" data-testid="dagen-grid">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left p-2 text-neutral-500 font-medium"> </th>
                    {DAGDELEN.map(dd => <th key={dd.value} className="p-2 text-neutral-500 font-medium text-xs">{dd.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {WEEKDAYS.map(w => (
                    <tr key={w.i}>
                      <td className="p-2 font-medium text-neutral-700">{w.label}</td>
                      {DAGDELEN.map(dd => {
                        const checked = form.dagen.some(d => d.weekday === w.i && d.dagdeel === dd.value);
                        return (
                          <td key={dd.value} className="p-2 text-center">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleDag(w.i, dd.value)}
                              data-testid={`dag-${w.i}-${dd.value}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 text-xs text-neutral-500">
              Geschat: <strong>{urenPerWeek} uur/week</strong> × € 9,65 = <strong>€ {(urenPerWeek * 9.65).toFixed(2)}/week</strong>
            </div>
            <div className="mt-4">
              <Label>Opmerkingen (optioneel)</Label>
              <Textarea rows={2} value={form.opmerkingen} data-testid="opmerkingen" onChange={e => setForm(f => ({ ...f, opmerkingen: e.target.value }))} />
            </div>
          </Card>
        )}

        {step === 4 && (
          <Card className="p-5 sm:p-8 bg-white shadow-none" data-testid="contract-preview">
            <div className="flex items-center gap-2 mb-4">
              <FileSignature className="h-5 w-5 text-[color:var(--sage)]" />
              <h1 className="font-display text-2xl sm:text-3xl">Contract & ondertekening</h1>
            </div>
            <div className="p-4 rounded-md border border-neutral-200 bg-neutral-50/50 text-sm space-y-3">
              <div><strong>Ouder(s):</strong> {form.ouder.voornaam} {form.ouder.achternaam}{form.partner && `, ${form.partner.voornaam} ${form.partner.achternaam}`}</div>
              <div><strong>Kind:</strong> {form.kind.voornaam} {form.kind.achternaam} · geb. {form.kind.geboortedatum}</div>
              <div><strong>Locatie:</strong> {locName}</div>
              <div><strong>Startdatum:</strong> {form.start_date}</div>
              <div><strong>Dagen ({urenPerWeek} u/wk):</strong>
                <ul className="list-disc list-inside">
                  {form.dagen.sort((a, b) => a.weekday - b.weekday).map((d, i) => (
                    <li key={i}>{WEEKDAYS[d.weekday].label} — {DAGDELEN.find(dd => dd.value === d.dagdeel)?.label}</li>
                  ))}
                </ul>
              </div>
              <div><strong>Tarief:</strong> € 9,65 per uur (indicatief, kan wijzigen bij mutaties)</div>
              <div className="text-xs text-neutral-500 pt-2 border-t">
                Door akkoord te gaan bevestigt u een overeenkomst tussen u en Webster.
                Wij mogen het aanbod aanpassen als de gewenste dagen/groep niet direct beschikbaar zijn.
                U kunt binnen 14 dagen de overeenkomst kosteloos ontbinden.
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <label className="flex items-start gap-3 text-sm">
                <Checkbox
                  checked={sign.akkoord}
                  onCheckedChange={v => setSign(s => ({ ...s, akkoord: !!v }))}
                  data-testid="akkoord-checkbox"
                />
                <span>Ik ga akkoord met bovenstaande overeenkomst en de <a href="#" className="underline">algemene voorwaarden</a>.</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Volledige naam ouder 1 *" testid="ondertekenaar-1"
                  value={sign.ondertekenaar_naam}
                  onChange={v => setSign(s => ({ ...s, ondertekenaar_naam: v }))}
                  placeholder={`${form.ouder.voornaam} ${form.ouder.achternaam}`} />
                {form.partner && (
                  <Field label="Volledige naam ouder 2" testid="ondertekenaar-2"
                    value={sign.ondertekenaar_2_naam}
                    onChange={v => setSign(s => ({ ...s, ondertekenaar_2_naam: v }))}
                    placeholder={`${form.partner.voornaam} ${form.partner.achternaam}`} />
                )}
              </div>
            </div>
          </Card>
        )}

        {step === 5 && signed && (
          <Card className="p-5 sm:p-8 bg-white shadow-none text-center" data-testid="success-card">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-emerald-100">
              <Check className="h-8 w-8 text-emerald-700" />
            </div>
            <h1 className="font-display text-3xl mb-2">Inschrijving voltooid</h1>
            <p className="text-neutral-600 mb-6">Bedankt {form.ouder.voornaam}! U ontvangt een bevestigingsmail met uw contract en (indien nieuw) login-gegevens voor het ouderportaal.</p>
            <div className="p-4 border-2 border-[color:var(--sage)] rounded-md inline-block text-left bg-[color:var(--sage-100)]/40" data-testid="digitaal-zegel">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="h-5 w-5 text-[color:var(--sage)]" />
                <div className="text-xs uppercase tracking-widest text-[color:var(--sage)] font-semibold">Digitaal zegel</div>
              </div>
              <div className="text-sm space-y-1">
                <div>Ondertekend op: <strong>{new Date(signed.ondertekend_op).toLocaleString('nl-NL')}</strong></div>
                <div>Ondertekenaar(s): <strong>{signed.ondertekenaar}{signed.ondertekenaar_2 ? ` & ${signed.ondertekenaar_2}` : ""}</strong></div>
                <div className="text-xs text-neutral-500">IP: {signed.ip || "—"}</div>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-neutral-500">
              <Sparkles className="h-3 w-3" /> Webster (Powered by Webster)
            </div>
          </Card>
        )}

        {step < 5 && (
          <div className="mt-6 flex items-center justify-between" data-testid="step-nav">
            <Button variant="ghost" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Vorige
            </Button>
            {step < 4 && (
              <Button onClick={nextStep} data-testid="next-step" style={{ background: "var(--sage)" }} className="text-white">
                Volgende <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 4 && (
              <Button onClick={submit} disabled={busy || !sign.akkoord || !sign.ondertekenaar_naam.trim()}
                data-testid="submit-inschrijving"
                style={{ background: "var(--terracotta)" }} className="text-white">
                {busy ? "Bezig…" : <><FileSignature className="h-4 w-4 mr-1" /> Ondertekenen & inschrijven</>}
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={value || ""} onChange={e => onChange(e.target.value)}
        data-testid={testid} placeholder={placeholder} />
    </div>
  );
}

function OuderVelden({ value, onChange, idPrefix }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Voornaam *" testid={`${idPrefix}-voornaam`} value={value.voornaam} onChange={v => set("voornaam", v)} />
      <Field label="Achternaam *" testid={`${idPrefix}-achternaam`} value={value.achternaam} onChange={v => set("achternaam", v)} />
      <Field label="E-mailadres *" type="email" testid={`${idPrefix}-email`} value={value.email} onChange={v => set("email", v)} />
      <Field label="Telefoon *" testid={`${idPrefix}-telefoon`} value={value.telefoon} onChange={v => set("telefoon", v)} />
      <Field label="Adres" testid={`${idPrefix}-adres`} value={value.adres} onChange={v => set("adres", v)} />
      <div className="grid grid-cols-2 gap-2">
        <Field label="Postcode" testid={`${idPrefix}-postcode`} value={value.postcode} onChange={v => set("postcode", v)} />
        <Field label="Woonplaats" testid={`${idPrefix}-stad`} value={value.stad} onChange={v => set("stad", v)} />
      </div>
      <Field label="IBAN (optioneel)" testid={`${idPrefix}-iban`} value={value.iban} onChange={v => set("iban", v)} />
    </div>
  );
}
